import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [showCreateMaestro, setShowCreateMaestro] = useState(false);
  const [maestroData, setMaestroData] = useState({
    nombre: "",
    email: "",
    password: ""
  });
  const [maestroLoading, setMaestroLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      onLogin(response.data.user, response.data.access_token);
      toast.success("Inicio de sesión exitoso");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la autenticación");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMaestro = async (e) => {
    e.preventDefault();
    
    if (!maestroData.nombre || !maestroData.email || !maestroData.password) {
      toast.error("Todos los campos son obligatorios");
      return;
    }

    if (maestroData.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setMaestroLoading(true);

    try {
      await axios.post(`${API}/auth/create-maestro`, maestroData);
      toast.success("Usuario maestro creado exitosamente. Ahora puedes iniciar sesión");
      setShowCreateMaestro(false);
      setMaestroData({ nombre: "", email: "", password: "" });
      // Auto-fill login with new credentials
      setFormData({ email: maestroData.email, password: maestroData.password });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario maestro");
    } finally {
      setMaestroLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Industrial Park Management" 
              className="h-16 w-auto"
            />
          </div>
          <CardTitle className="text-2xl">
            Iniciar Sesión
          </CardTitle>
          <CardDescription>
            Sistema de Gestión de Contratos de Arrendamiento
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="usuario@ejemplo.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? (
                "Procesando..."
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Iniciar Sesión
                </>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm space-y-2">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-purple-600 hover:text-purple-700 font-medium block w-full"
              data-testid="forgot-password-button"
            >
              ¿Olvidaste tu contraseña?
            </button>
            <div className="border-t pt-2">
              <button
                type="button"
                onClick={() => setShowCreateMaestro(true)}
                className="text-purple-600 hover:text-purple-700 font-medium block w-full"
              >
                👑 Crear Usuario Maestro
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Maestro Modal */}
      {showCreateMaestro && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                👑 Crear Usuario Maestro
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                El usuario maestro tiene control total del sistema
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateMaestro} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="maestro-nombre">Nombre completo</Label>
                  <Input
                    id="maestro-nombre"
                    type="text"
                    value={maestroData.nombre}
                    onChange={(e) => setMaestroData({ ...maestroData, nombre: e.target.value })}
                    required
                    placeholder="Juan Pérez"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maestro-email">Email</Label>
                  <Input
                    id="maestro-email"
                    type="email"
                    value={maestroData.email}
                    onChange={(e) => setMaestroData({ ...maestroData, email: e.target.value })}
                    required
                    placeholder="usuario@ejemplo.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maestro-password">Contraseña</Label>
                  <Input
                    id="maestro-password"
                    type="password"
                    value={maestroData.password}
                    onChange={(e) => setMaestroData({ ...maestroData, password: e.target.value })}
                    required
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    ⚠️ <strong>Advertencia:</strong> Este usuario tendrá acceso completo al sistema y podrá gestionar todos los usuarios, contratos y datos.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowCreateMaestro(false);
                      setMaestroData({ nombre: "", email: "", password: "" });
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1" 
                    disabled={maestroLoading}
                  >
                    {maestroLoading ? "Creando..." : "Crear Maestro"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
